/**
 * Tenant Admin: create a General Knowledge bucket. Three decisions — a name
 * (display only; renames never touch content) and which GROUPS can read it
 * (none = everyone who can search the KB). Audience is membership: to give
 * someone access, add them to one of the bucket's groups. Who can ADD content
 * is the platform's authoring rule, not per-bucket config.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Banner, Button, Input, Modal } from '@servicecompressionllc/sc-design-system';
import { knowledgeApi } from './api.js';

const CheckRow = ({
  checked,
  label,
  note,
  onToggle,
}: {
  checked: boolean;
  label: string;
  note?: string;
  onToggle: () => void;
}) => (
  <label className="flex cursor-pointer items-center gap-2.5">
    <input type="checkbox" checked={checked} onChange={onToggle} aria-label={label} />
    <span className="text-sm text-ink">{label}</span>
    {note !== undefined && <span className="text-xs text-ink-subtle">{note}</span>}
  </label>
);

export const CreateBucketModal = ({
  open,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: { name: string; readGroupIds: string[] }) => void;
}) => {
  const [name, setName] = useState('');
  const [readGroupIds, setReadGroupIds] = useState<string[]>([]);
  const groups = useQuery({
    queryKey: ['knowledge', 'bucketGroups'],
    queryFn: knowledgeApi.groups,
    enabled: open,
  });

  const close = () => {
    setName('');
    setReadGroupIds([]);
    onClose();
  };
  const toggleGroup = (id: string) =>
    setReadGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));

  return (
    <Modal open={open} onOpenChange={(o) => !o && close()}>
      <Modal.Header>
        <Modal.Title>
          <span className="flex items-center gap-2.5">
            New bucket
            <Badge variant="neutral">Tenant Admin</Badge>
          </span>
        </Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="bucket-name"
              className="text-xs font-semibold uppercase tracking-wide text-ink-subtle"
            >
              Name
            </label>
            <Input
              id="bucket-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Finance"
              aria-label="Bucket name"
              className="w-full"
            />
            <span className="text-xs text-ink-subtle">
              You can rename a bucket later without affecting its content or access.
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
              Which groups can read it
            </span>
            <div className="flex flex-col gap-2 rounded-md border border-outline bg-surface p-3">
              {(groups.data?.groups ?? []).length === 0 ? (
                <span className="text-sm text-ink-subtle">
                  No groups yet. Create one on the Groups page to restrict a bucket.
                </span>
              ) : (
                (groups.data?.groups ?? []).map((g) => (
                  <CheckRow
                    key={g.id}
                    checked={readGroupIds.includes(g.id)}
                    label={g.name}
                    note={`${String(g.memberCount)} ${g.memberCount === 1 ? 'member' : 'members'}`}
                    onToggle={() => toggleGroup(g.id)}
                  />
                ))
              )}
            </div>
            <span className="text-xs text-ink-subtle">
              With no groups selected, everyone who can search the knowledge base can read it.
              Membership is the access: add or remove people on the Groups page.
            </span>
          </div>

          {error !== null && (
            <Banner variant="danger">
              <Banner.Description>{error}</Banner.Description>
            </Banner>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button
          disabled={name.trim().length === 0 || busy}
          onClick={() => onSubmit({ name: name.trim(), readGroupIds })}
        >
          {busy ? 'Creating…' : 'Create bucket'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
